import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Optional,
  Output,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import {
  FormErrorDirective,
  FormFieldComponent,
  IconButtonComponent,
  IconHelpComponent,
  InputComponent,
  LabelComponent,
  RtTooltip,
} from '@recruitee/user-interface';

import { DynamicHelpers } from '../../../helpers';
import {
  AnyDynamicCommonInputConfig,
  DynamicEmailInputConfig,
  DynamicInputConfigType,
  DynamicTextInputConfig,
} from '../../../types';
import { AdminSelectInputComponent } from '../../admin-select-input/admin-select-input.component';
import { AdminsInputComponent } from '../../admins-input/admins-input.component';
import { CandidateEmailInputComponent } from '../../candidate-email-input/candidate-email-input.component';
import { CheckboxInputComponent } from '../../checkbox-input/checkbox-input.component';
import { CountrySelectInputComponent } from '../../country-select-input/country-select-input.component';
import { CustomInputComponent } from '../../custom-input/custom-input.component';
import { DateDropdownsInputComponent } from '../../date-dropdowns-input/date-dropdowns-input.component';
import { DatePickerInputComponent } from '../../date-picker-input/date-picker-input.component';
import { DynamicSelectInputComponent } from '../../dynamic-select-input/dynamic-select-input.component';
import { FormLoaderInputComponent } from '../../form-loader/form-loader-input.component';
import { GeneratedPasswordInputComponent } from '../../generated-password-input/generated-password-input.component';
import { PlacementInputComponent } from '../../placement-input/placement-input.component';
import { PrependInputAdapterComponent } from '../../prepend-input-adapter/prepend-input-adapter.component';
import { RegionSelectInputComponent } from '../../region-select-input/region-select-input.component';
import { ResourceSelectInputComponent } from '../../resource-select-input/resource-select-input.component';
import { DynamicSubdomainInputComponent } from '../../subdomain-input-adapter/dynamic-subdomain-input.component';
import { TimePickerInputComponent } from '../../time-picker-input/time-picker-input.component';
import { DynamicFormEventService } from '../dynamic-form-event.service';
import { DynamicTypeaheadInputComponent } from '../dynamic-typeahead-input/dynamic-typeahead-input.component';
import { DynamicFormInput } from './dynamic-form-input';

@Component({
  selector: 'rti-dynamic-form-input',
  templateUrl: './dynamic-form-input.component.html',
  styleUrls: ['./dynamic-form-input.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    // eslint-disable-next-line quotes
    '[style.width]': 'getFieldWidth()',
  },
  imports: [
    AdminSelectInputComponent,
    AdminsInputComponent,
    CandidateEmailInputComponent,
    CheckboxInputComponent,
    CountrySelectInputComponent,
    CustomInputComponent,
    DateDropdownsInputComponent,
    DynamicSubdomainInputComponent,
    DatePickerInputComponent,
    DynamicSelectInputComponent,
    DynamicTypeaheadInputComponent,
    FormErrorDirective,
    FormFieldComponent,
    FormLoaderInputComponent,
    GeneratedPasswordInputComponent,
    IconButtonComponent,
    IconHelpComponent,
    InputComponent,
    LabelComponent,
    PlacementInputComponent,
    ReactiveFormsModule,
    RegionSelectInputComponent,
    ResourceSelectInputComponent,
    RtTooltip,
    TimePickerInputComponent,
    PrependInputAdapterComponent,
  ],
})
export class DynamicFormInputComponent {
  public DynamicInputConfigType: typeof DynamicInputConfigType = DynamicInputConfigType;

  @Input() public input: DynamicFormInput;
  @Output() public remove: EventEmitter<DynamicFormInput> = new EventEmitter();
  constructor(
    public helpers: DynamicHelpers,
    @Optional() private dynamicFormService: DynamicFormEventService,
  ) {}

  public getFieldWidth(): string {
    return `${this.input.config.widthPercent || 100}%`;
  }

  public clickRemove(): void {
    this.remove.emit(this.input);
  }

  public isInput(value: AnyDynamicCommonInputConfig): value is DynamicTextInputConfig {
    return true;
  }

  public isEmail(value: AnyDynamicCommonInputConfig): value is DynamicEmailInputConfig {
    return true;
  }

  public showRequiredAsterisk(input: DynamicFormInput): boolean {
    if (!input.config.required) return false;

    if (input.type === 'date_dropdowns') {
      return !(input.control.touched && this.input.control.valid);
    }

    if (input.control.value === null || input.control.value === undefined) {
      return true;
    } else if (typeof input.control.value === 'string') {
      return !input.control.value.trim();
    } else if (Array.isArray(input.control.value)) {
      return !input.control.value.length;
    } else {
      return false;
    }
  }

  public onEnter(): void {
    this.dynamicFormService.submit$.next();
  }
}
